import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QRCode from 'react-qr-code';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import { noop, find } from 'lodash';
import i18n from 'i18n';
import apiCubic from 'config/api-cubic';
import { hideLoader, showLoader } from 'actions/loader';
import { fetchBalance } from 'actions/profile';
import { getIsLifecellFlag, getProfileData } from 'reducers/profile/selectors';
import FocusableParent from 'components/navigation/FocusableParent';
import Button from 'components/Button';
import { createPaymentLink } from './api';
import requester from 'utils/requester';
import api from 'config/api';
import {
  StyledModalContainer,
  StyledTitle,
  StyledDescription,
  StyledHighlight,
  StyledBlockWrapper,
  StyledLeftContent,
  StyledButtonWrapper,
  StyledSmallButtonText,
} from '../StyledComponents';

const DATA_GROUP_FIRMS = ['data_group', 'gts'];
const PREPAID_CONTRACT_TYPES = ['prepaid', 'prepay'];

const ExtendedBlockWrapper = styled(StyledBlockWrapper)`
  padding: ${scaleSize(60)}px ${scaleSize(80)}px;
`;

const ExtendedTitle = styled(StyledTitle)`
  min-height: ${scaleSize(114)}px;
  padding: 0 ${scaleSize(15)}px ${scaleSize(40)}px 0;
  margin: 0;
  font-size: ${scaleSize(38)}px;
  text-align: left;
`;

const ExtendedDescription = styled(StyledDescription)`
  height: auto;
  padding-bottom: ${scaleSize(30)}px;
  text-align: left;
`;

const StyledTerms = styled.p`
  color: ${({ theme }) => theme.iconDefaultColor};
  text-align: left;
  margin: ${scaleSize(15)}px 0;
  font-size: ${scaleSize(20)}px;
`;

const StyledRightContent = styled.div`
  margin-top: ${scaleSize(15)}px;
  width: 40%;
  float: right;
  text-align: center;
`;

const PortmoneModal = ({
  title,
  cost,
  isTVOD = false,
  dueDate,
  contractCode,
  onModalConfirm = noop,
  onModalBack = noop,
}) => {
  const dispatch = useDispatch();
  const intervalRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [QRLink, setQRLink] = useState('');
  const { customerRef } = useSelector(getProfileData);
  const isLifecellUser = useSelector(getIsLifecellFlag);
  const cardLabelText = isTVOD ? i18n.t('portmone.cardSaveLabelTVOD') : i18n.t('portmone.cardSaveLabel');
  const costsTitle = isLifecellUser ? i18n.t('portmone.costIsLifecell') : i18n.t('portmone.costIs');

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        dispatch(showLoader());

        let isTVODforDataGroup = isTVOD;

        const { data } = await apiCubic.post({
          endpoint: apiCubic.endpoints.getContractInfo,
          data: { contractCode },
        });

        const isDataGroupUser = DATA_GROUP_FIRMS.includes(data.firmName);
        const isOFNETUser =
          !isDataGroupUser && data.contractTypes.some((type) => PREPAID_CONTRACT_TYPES.includes(type));
        const isONNETUser = !isOFNETUser;
        let payeeId = '23104';
        let description = contractCode;

        if (isONNETUser) isTVODforDataGroup = true;

        if (isDataGroupUser) {
          isTVODforDataGroup = true;
          const { data: paymentSystems } = await apiCubic.post({
            endpoint: apiCubic.endpoints.getPaymentSystems,
            data: { contractCode },
          });

          const portmoneConfig = find(paymentSystems.payload, { paymentSystemName: 'portmone' });

          if (portmoneConfig?.method === 'POST') {
            payeeId = portmoneConfig.payee_id;
            description = portmoneConfig.description;
          } else {
            //SMRT-274
            // Prevent unmounted setState
            setTimeout(onModalConfirm, 0);
            return;
          }
        }

        try {
          const { linkPayment } = await createPaymentLink({
            cost,
            contractCode,
            customerRef,
            payeeId,
            description,
            isTVOD: isTVODforDataGroup,
          });
          setQRLink(linkPayment);
        } catch {}
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
        dispatch(hideLoader());
      }
    };

    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let prevBalance = null;
    const checkBalance = async function () {
      const data = await requester({
        method: 'get',
        url: `${api.host}${api.endpoints.balance}`,
        appendParams: false,
      });

      const balance = data.balance;

      if (prevBalance !== null && balance !== prevBalance) {
        onModalConfirm();
        //Update user balance in whole app on successful top up
        dispatch(fetchBalance());
      }

      prevBalance = balance;
    };

    intervalRef.current = setInterval(checkBalance, 10 * 1000);

    return () => clearInterval(intervalRef.current);
  }, [dispatch, onModalConfirm]);

  if (isLoading) return null;

  return (
    <StyledModalContainer>
      <ExtendedBlockWrapper>
        <StyledLeftContent>
          <ExtendedTitle>
            {costsTitle}: {cost} {i18n.t('portmone.currency')}
          </ExtendedTitle>
          <ExtendedDescription>
            {i18n.t('portmone.youreOrdering')}: <br />
            <StyledHighlight>{title}</StyledHighlight> <br />
            {dueDate && (
              <>
                {i18n.t('portmone.validDuration')} <StyledHighlight>{dueDate}</StyledHighlight>
              </>
            )}
            <p> {i18n.t('portmone.scanQR')}.</p>
          </ExtendedDescription>
          <FocusableParent
            config={{
              leaveFor: {
                right: '',
                up: '',
                left: '',
                down: '',
              },
            }}
            defaultSection
          >
            <StyledButtonWrapper>
              <Button onEnter={onModalBack} focusOnMouseEnter>
                <StyledSmallButtonText>{i18n.t('common.cancel')}</StyledSmallButtonText>
              </Button>
            </StyledButtonWrapper>
          </FocusableParent>
        </StyledLeftContent>
        <StyledRightContent>
          {QRLink && <QRCode value={QRLink} size={scaleSize(450)} />}
          <div>
            <StyledTerms> {i18n.t('portmone.closeAfter')}</StyledTerms>
            <StyledTerms> {cardLabelText}</StyledTerms>
          </div>
        </StyledRightContent>
      </ExtendedBlockWrapper>
    </StyledModalContainer>
  );
};

export default PortmoneModal;
